/**
 * Cloud cells: the weather model's convective cells drawn as things.
 *
 * The water cycle (server/nism/wxcycle.py) keeps its clouds as cells: each has an identity, a place, a size, a base and a top, the water it
 * holds, and the rain it drops. The "cloud_cells" message lists them (about 2 Hz). This module keeps the list, moves each cell by its velocity
 * between messages, and draws every cell near the eye in one of two ways, so they can be compared on the real scene:
 *
 *   puffs    each cell is a cluster of soft lit sprites (instanced, sorted far to near, lit analytically). Cells nearer than FAR_NEAR_M are drawn
 *            as they are; the cells beyond it are baked (see below), so the cost of the far clouds no longer scales with their overdraw.
 *   direct   the same puffs with nothing baked: every cell drawn every frame (the cost is overdraw: 20 to 40 times the screen's area on a cloudy sky).
 *   volume   each cell is a bounded volume (an oriented box round a half ellipsoid with a flat base), raymarched with a 3D noise erosion and
 *            a few taps toward the light. Better look, cost is pixels covered times steps.
 *   hybrid   the nearest few cells as volumes, the rest as puffs.
 *   off      no cells (the cloud volume, cloudVolume.ts, draws the cloud and the rain under it from the server's windows).
 *
 * The cells are the weather model's own objects, drawn here for comparison with the volume (the default is off: drawn with it they would double the cloud). Where they draw they are
 * lit, toned and hazed with the sky's uniforms (Sky.cellUniforms), so they match it.
 *
 * Depth. The camera's far plane (a few km) is shorter than the cells' range, so cells are projected onto the far plane (as the dome is) and
 * depth tested there: anything the scene draws in front of them (palms, terrain, sea) hides them, and they never hide it.
 *
 * The weather model's domain is periodic (tile_m), so cells are drawn at the nine tile offsets that fall within range.
 *
 * The far clouds are baked. Beyond a few km a cloud barely changes in the eye's view from one second to the next: it moves a few pixels in a
 * minute, and the eye moving by tens of metres shifts it by less than a pixel or two. So all the cells beyond FAR_NEAR_M are drawn, with the same
 * puff shader, into a cube of six faces (kept as one 3x2 atlas), painted far to near so they merge into one picture of the far sky; and each frame
 * the sky is one pass that looks that picture up. A face is drawn again when what it holds has drifted by BAKE_ERR_PX screen pixels (the clouds'
 * own motion, the eye's movement), when the light or the exposure has changed, or when a cell has crossed the near/far boundary; the new face
 * is cross-faded in over BAKE_XFADE_S seconds, and at most BAKE_MAX_FACES faces are redrawn in a frame.
 */
import * as THREE from 'three';
import type { Sky } from './sky';
import { NOISE_GLSL, SKY_VIEW_GLSL } from './sky';
import { CLOUD_BEAM_GLSL } from './atmosphereGpu';
import { AMBIENT_SH_GLSL } from './ambientSh';
import { cloudOptics } from './cloudOptics';
import { SCOTOPIC, SCOTOPIC_ROD_W, SCOTOPIC_TINT_N } from './exposure';

export type CellMode = 'off' | 'puffs' | 'volume' | 'hybrid' | 'direct';

/** The "cloud_cells" message: one row per cell, in the column order named by `fields`. Positions are world metres (the client's three x-z plane), the
 *  model's tile centred on the island; velocities in m/s; r the cell's radius; base and top heights above the sea; lwp in-cloud liquid water path g/m2;
 *  rain mm/h at the surface (mean over the cell's raining columns); age seconds; optional rr, rx, rz: the raining area as a disc, its radius and its offset
 *  from the cell's centre (rain falls in cores far smaller than the cell); optional hi: 1 for a cell of the outflow cloud (the cloud layer's own thin sheet,
 *  higher up, no rain), whose ids are counted apart from the cumulus cells'. */
export interface CloudCellsMessage { type: 'cloud_cells'; t: number; tile_m: number; fields: string[]; cells: number[][] }

/** Ground distance (m) over which cells fade out and the dome's low layer fades in. */
export const CELL_RANGE_NEAR = 13000, CELL_RANGE_FAR = 17000;
const VOLUME_RANGE = 5500;             // hybrid mode: cells nearer than this (ground distance) are volumes ...
const VOLUME_MAX = 6;                  // ... at most this many of them
const PUFF_CAP = 16384, VOL_CAP = 512, RAIN_CAP = 256;
const RAIN_MIN = 0.4;                  // mm/h: a cell raining less than this draws no curtain
const RAIN_LEAN_M = 400;               // the curtain's foot trails the cloud's base by this much against the cell's drift (as the dome's curtains do)
const RAIN_K = 0.001;                  // extinction per metre at 1 mm/h (the dome's curtains: 1 per km)
const RAIN_GAIN = 3;                   // times that: the model's showers are light (cores of 0.4 to 2 mm/h, a few hundred metres across) and barely show at 1
const FADE_S = 8;                      // seconds a new cell takes to grow in, and a cell that left the list to fade out
const SMOOTH_S = 1.5;                  // seconds the shown size follows the message
const SUN_ABSORB = 0.035;              // the puff shader's and the neighbour shading's extinction of the beam, per unit of optical depth

// The far-field bake
const FAR_NEAR_M = 2500;               // cells nearer than this (ground distance, nearest tile image) are drawn directly, the rest are baked
const FAR_HYST_M = 350;                // a cell changes side only past this much beyond the boundary
const BAKE_FACE = 1024;                // texels per face of the baked sky (a face covers 96 degrees: about 1.7 screen pixels per texel on a 1350 px high view)
const BAKE_FOV = 96;                   // degrees per face: over 90, so that bilinear sampling and the blend across a face's edge stay inside what was drawn
const BAKE_PUFF_CAP = 16384;
const BAKE_ERR_PX = 5;                 // redraw a face when the picture it holds is out by this many screen pixels
const BAKE_MAX_AGE_S = 24;             // and at the latest after this long (the cells' sizes and tops change slowly)
const BAKE_FADING_AGE_S = 2.5;         // while a cell in the face is still growing in or fading out
const BAKE_XFADE_S = 2.0;              // a redrawn face is cross-faded in over this time
const BAKE_HOLD_S = 4.5;               // a cell that crossed the boundary is drawn both ways this long
const BAKE_MAX_FACES = 1;              // faces drawn per frame (two while the sky is still empty)
const BAKE_LIGHT_TOL = 0.02;           // redraw all faces when the light or the exposure moved this much (relative)

// ------------------------------------------------------------------------------------------------------------------------------------
// The store

interface Cell {
  id: number;                                                                                // the store's key: the message's id, negated for an outflow cell
  hi: boolean;                                                                              // an outflow cell: a thin sheet high up, drawn as puffs only
  x0: number; z0: number; vx: number; vz: number; t0: number; ox: number; oz: number;     // position at the last message, velocity, offset that decays
  r: number; base: number; top: number; lwp: number; rain: number;                          // shown (eased)
  rr: number; rx: number; rz: number;                                                        // the raining area: radius, offset from the centre
  tr: number; tbase: number; ttop: number; tlwp: number; train: number;                     // targets from the last message
  trr: number; trx: number; trz: number;
  sa: number; tsa: number;                                                                   // area (m2) of the cloud system the cell is part of, shown and target
  pres: number; alive: boolean;
  seed: number;
  puffs: Float32Array | null;                                                               // layout: (nx, nh, nz, ns) per puff
  zone: number; zt: number;                                                                  // ZONE_*: drawn directly, baked, or crossing; and when it last changed (the cloud-cells clock)
}

const ZONE_NEAR = 0, ZONE_FAR = 1, ZONE_ENTERING = 2, ZONE_LEAVING = 3;

function mulberry32(a: number) {
  return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

const PS = 5;                          // floats per puff in a layout: nx, nh, nz, ns, kind

/** The layout of an outflow cell's puffs: a thin sheet. Broad, soft puffs spread over the whole disc in two shallow layers; no cauliflower. */
function sheetLayout(seed: number, r: number): Float32Array {
  const rnd = mulberry32(seed * 2654435761 >>> 0);
  const n = Math.max(10, Math.min(56, Math.round(10 + 40 * r / 1000)));
  const out = new Float32Array(n * PS);
  for (let i = 0; i < n; i++) {
    const rho = Math.sqrt(rnd()) * 0.92, th = rnd() * Math.PI * 2;
    const nh = (i & 1 ? 0.15 : 0.5) + 0.2 * rnd() - 0.1 * rho;
    const ns = (0.30 - 0.12 * rho) * (0.8 + 0.4 * rnd());
    out[i * PS] = rho * Math.cos(th); out[i * PS + 1] = Math.max(0, Math.min(1, nh)); out[i * PS + 2] = rho * Math.sin(th); out[i * PS + 3] = Math.max(0.12, ns); out[i * PS + 4] = 0;
  }
  return out;
}

/** The layout of a cell's puffs in the cell's own unit frame: nx, nz across (the cell's radius is 1), nh up (the cell's thickness is 1), ns the puff's
 *  radius as a share of the cell, kind 1 for a body puff. Three scales, so it does not read as a heap of equal balls: a flat base of broad puffs, a few
 *  large soft body puffs filling the middle, and many small puffs only on the dome's surface (the cauliflower rind, which is where a cumulus has detail). */
function puffLayout(seed: number, r: number): Float32Array {
  const rnd = mulberry32(seed * 2654435761 >>> 0);
  const n = Math.max(20, Math.min(84, Math.round(20 + 44 * r / 1000)));
  const nBase = Math.round(n * 0.14), nBody = Math.round(n * 0.2);
  const out = new Float32Array(n * PS);
  for (let i = 0; i < n; i++) {
    let rho: number, nh: number, ns: number, kind = 0;
    const th = rnd() * Math.PI * 2;
    if (i < nBase) { rho = Math.sqrt(rnd()) * 0.9; nh = 0.05 + 0.1 * rnd(); ns = 0.32 - 0.08 * rho + 0.05 * rnd(); kind = 1; }
    else if (i < nBase + nBody) {
      rho = Math.sqrt(rnd()) * 0.62; const dome = Math.sqrt(Math.max(0, 1 - rho * rho));
      nh = (0.15 + 0.5 * rnd()) * dome; ns = 0.40 + 0.22 * rnd(); kind = 1;
    } else {
      rho = Math.sqrt(rnd()) * 0.86; const dome = Math.sqrt(Math.max(0, 1 - rho * rho));
      nh = dome * (0.78 + 0.22 * rnd()); ns = 0.08 + 0.2 * Math.pow(rnd(), 1.6);                      // mostly small, a few larger lumps
    }
    out[i * PS] = rho * Math.cos(th); out[i * PS + 1] = Math.min(1, nh); out[i * PS + 2] = rho * Math.sin(th); out[i * PS + 3] = Math.max(0.07, ns); out[i * PS + 4] = kind;
  }
  return out;
}

export class CellStore {
  cells = new Map<number, Cell>();
  time = 0;
  tile = 25600;

  ingest(msg: CloudCellsMessage) {
    const f = msg.fields, ix = (k: string) => f.indexOf(k);
    const J = { rr: ix('rr'), rx: ix('rx'), rz: ix('rz'), hi: ix('hi'), sysa: ix('sysa') };                 // optional: a message without them gets a core of 0.6 of the cell's radius at its centre
    const I = { id: ix('id'), x: ix('x'), z: ix('z'), vx: ix('vx'), vz: ix('vz'), r: ix('r'), base: ix('base'), top: ix('top'), lwp: ix('lwp'), rain: ix('rain') };
    if (Object.values(I).some(v => v < 0)) { console.warn('[cloud cells] message without a required field', f); return; }
    this.tile = msg.tile_m;
    const seen = new Set<number>();
    for (const row of msg.cells) {
      const hi = J.hi >= 0 && row[J.hi] > 0.5, sa = J.sysa >= 0 ? row[J.sysa] : 0;
      const id = hi ? -row[I.id] : row[I.id]; seen.add(id);                 // the two populations number their cells apart
      const rr = J.rr >= 0 ? row[J.rr] : (row[I.rain] > 0 ? 0.6 * row[I.r] : 0), rx = J.rx >= 0 ? row[J.rx] : 0, rz = J.rz >= 0 ? row[J.rz] : 0;
      let c = this.cells.get(id);
      if (!c) {
        c = { id, hi, x0: row[I.x], z0: row[I.z], vx: row[I.vx], vz: row[I.vz], t0: this.time, ox: 0, oz: 0,
          r: row[I.r], base: row[I.base], top: row[I.top], lwp: row[I.lwp], rain: row[I.rain],
          rr, rx, rz, trr: rr, trx: rx, trz: rz, sa, tsa: sa,
          tr: row[I.r], tbase: row[I.base], ttop: row[I.top], tlwp: row[I.lwp], train: row[I.rain], pres: 0, alive: true, seed: (hi ? (-id) ^ 0x5bd1e995 : id) >>> 0, puffs: null, zone: -1, zt: 0 };
        this.cells.set(id, c);
      } else {
        const p = this.position(c);                       // where it is drawn now; the new fix is reached smoothly
        const T = this.tile;                              // the model's tile is periodic: a cell that crossed its edge is a tile away in these coordinates, not a tile of ground to slide over
        c.ox = p[0] - row[I.x]; c.oz = p[1] - row[I.z];
        c.ox -= Math.round(c.ox / T) * T; c.oz -= Math.round(c.oz / T) * T;
        c.x0 = row[I.x]; c.z0 = row[I.z]; c.vx = row[I.vx]; c.vz = row[I.vz]; c.t0 = this.time;
        c.tr = row[I.r]; c.tbase = row[I.base]; c.ttop = row[I.top]; c.tlwp = row[I.lwp]; c.train = row[I.rain]; c.trr = rr; c.trx = rx; c.trz = rz; c.tsa = sa; c.alive = true;
      }
    }
    for (const c of this.cells.values()) if (!seen.has(c.id)) c.alive = false;
  }

  position(c: Cell): [number, number] {
    const dt = this.time - c.t0, k = Math.exp(-dt / 0.8);
    return [c.x0 + c.vx * dt + c.ox * k, c.z0 + c.vz * dt + c.oz * k];
  }

  update(dt: number) {
    this.time += dt;
    const k = Math.min(1, dt / SMOOTH_S), step = dt / FADE_S;
    for (const c of this.cells.values()) {
      c.r += (c.tr - c.r) * k; c.base += (c.tbase - c.base) * k; c.top += (c.ttop - c.top) * k; c.lwp += (c.tlwp - c.lwp) * k; c.rain += (c.train - c.rain) * k;
      c.rr += (c.trr - c.rr) * k; c.rx += (c.trx - c.rx) * k; c.rz += (c.trz - c.rz) * k; c.sa += (c.tsa - c.sa) * Math.min(1, dt / 4);
      c.pres = c.alive ? Math.min(1, c.pres + step) : Math.max(0, c.pres - step);
      if (!c.alive && c.pres <= 0) this.cells.delete(c.id);
    }
  }
}

// ------------------------------------------------------------------------------------------------------------------------------------
// What the two renderers share (a mirror of the dome's cloud lighting in sky.ts: whiten, scoto, tone, the lit table, the sky colour for the haze)

const SHARED_GLSL = /* glsl */`
precision highp float;
uniform vec3  uSunDir;
uniform vec3  uSrc;
uniform vec3  uAmb;
${AMBIENT_SH_GLSL}
uniform float uGain;
uniform vec2  uCgain;
uniform float uNight;
uniform float uHpSky;
uniform float uHpCloud;
uniform vec3  uLDir;
uniform float uLUp;
uniform float uWB;
uniform vec3  uMoonSrc;
uniform float uSrgb;
uniform float uFrame;
uniform vec2  uRange;       // cells fade out between these ground distances (the dome's low layer fades in)
const float PI = 3.14159265;
${SKY_VIEW_GLSL}
${CLOUD_BEAM_GLSL}
const vec3 ROD  = vec3(${SCOTOPIC_ROD_W.map(v => v.toFixed(5)).join(', ')});
const vec3 TINT = vec3(${SCOTOPIC_TINT_N.map(v => v.toFixed(5)).join(', ')});
const float SCOT = ${SCOTOPIC.STRENGTH.toFixed(3)};
const float NIGHT_AMB = 1.8;
const float HAZE_RANGE = 30000.0;
const float VERT_DARK = 0.7;
${NOISE_GLSL}
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
vec3 whiten(vec3 c) { return mix(c, vec3(luma(c)), uWB); }
vec3 scoto(vec3 c) { float rod = dot(c, ROD); return c + (rod * TINT - c) * (SCOT * uNight); }
vec3 srgbToLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c)); }
vec3 tone(vec3 x, float hp) {
  vec3 a = 1.0 - exp(-x);
  float m = max(max(x.r, x.g), max(x.b, 1e-6));
  return mix(a, x / m * (1.0 - exp(-m)), hp);
}
// the sky's look-up by the view direction (the sunlight on a cloud is cloudBeam: atmosphereGpu.ts)
vec3 skyTone(vec3 d) { return tone(scoto(skyLinear(d)) * uGain, uHpSky); }
// A cloud's colour from the share of the beam that reaches it (0..1, self-shadowing included), the ambient factor, and a shade multiplier.
vec3 cloudColor(float beamT, float ambF, float shade, vec3 d, float dist) {
  vec3 lit = whiten(uSrc * cloudBeam(d, dist)) + uMoonSrc;
  float fwd = 1.0 + 0.9 * pow(max(dot(d, uLDir), 0.0), 6.0);
  vec3 litF = mix(lit, vec3(luma(lit)), 0.5);
  vec3 direct = fwd * (lit * (0.95 * beamT) + litF * 0.14);
  vec3 amb = uAmb * ambField(d, 0.7) * ambF * (1.0 + NIGHT_AMB * uNight);      // (a puff has no height of its own here: a little more up than down)
  vec3 cdt = tone(scoto(direct) * uCgain.x * shade + scoto(amb) * uGain * (0.7 + 0.3 * shade), uHpCloud);
  cdt = mix(cdt, skyTone(d), 0.9 * (1.0 - exp(-dist / HAZE_RANGE)));
  return mix(cdt, srgbToLinear(cdt), uSrgb * (1.0 - smoothstep(0.35, 1.0, uNight)));
}
float rangeFade(vec3 world) {
  float g = length(world.xz - cameraPosition.xz);
  return 1.0 - smoothstep(uRange.x, uRange.y, g);
}
`;

// ------------------------------------------------------------------------------------------------------------------------------------
// Puffs

const PUFF_VERT = /* glsl */`
attribute vec4 aPos;      // world centre, radius
attribute vec4 aLoc;      // position in the cell's unit frame (x, h, z), tau of the cell
attribute vec4 aMeta;     // alpha factor, seed, cell radius / thickness, cell tau
attribute float aShade;   // the share of the beam that reaches the cell past its neighbours
attribute vec2 aBase;     // the cell's base height (m), and the depth (m) over which the base's edge is soft
varying float vShade;
varying float vH; varying float vSoft;
varying vec2 vUv; varying vec3 vWorld; varying vec3 vLoc; varying vec4 vMeta; varying float vSheet;
void main() {
  vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  vec3 w = aPos.xyz + (right * position.x + up * position.y) * aPos.w * 1.3;
  vUv = position.xy * 1.3; vWorld = aPos.xyz; vLoc = aLoc.xyz; vSheet = aLoc.w; vMeta = aMeta; vShade = aShade;
  vH = w.y - aBase.x; vSoft = aBase.y;                       // this corner's height above the cell's base (linear across the sprite, so exact per pixel)
  vec4 cp = projectionMatrix * viewMatrix * vec4(w, 1.0);
  gl_Position = vec4(cp.xy, cp.w * 0.9999, cp.w);        // on the far plane: the scene's own depth decides what hides it
}`;

const PUFF_FRAG = SHARED_GLSL + /* glsl */`
varying vec2 vUv; varying vec3 vWorld; varying vec3 vLoc; varying vec4 vMeta; varying float vSheet; varying float vShade; varying float vH; varying float vSoft;
// exit distance of a ray from a point inside the unit half-ellipsoid (unit sphere in the cell's frame)
float sphereExit(vec3 p, vec3 l) { float b = dot(p, l), c = dot(p, p) - 1.0; return max(-b + sqrt(max(b * b - c, 0.0)), 0.0); }
void main() {
  float r = length(vUv);
  if (r > 1.3) discard;
  vec3 cam = cameraPosition;
  vec3 pv = vWorld - cam;
  float dist = length(pv);
  vec3 d = pv / dist;
  vec3 np = vec3(vUv * 2.0, vMeta.y * 13.0);
  float n = vnoise(np) * 0.6 + vnoise(np * 2.3 + 5.0) * 0.4;
  float edge = r + (n - 0.5) * 0.7;
  float cov = 1.0 - smoothstep(0.55, 1.0, edge);
  if (cov < 0.004) discard;
  vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
  float zz = sqrt(max(1.0 - min(r, 1.0) * min(r, 1.0), 0.0));
  vec3 Ns = normalize(right * vUv.x + up * vUv.y - d * zz);
  vec3 Nc = normalize(vec3(vLoc.x, (vLoc.y - 0.15) * 1.4, vLoc.z) + vec3(0.0, 0.12, 0.0));
  Nc = normalize(mix(Nc, vec3(0.0, 1.0, 0.0), vSheet * 0.75));            // a thin sheet is lit from above and below alike, not shaped like a dome
  vec3 N = normalize(mix(Ns, Nc, 0.4) + 0.4 * vec3(n - 0.5, vnoise(np + 7.0) - 0.5, vnoise(np + 13.0) - 0.5));
  float h = clamp(vLoc.y, 0.0, 1.0);
  float lam = clamp(0.36 + 0.64 * dot(N, uLDir), 0.0, 1.0);
  // how deep this puff sits inside the cell along the light: the unit frame's scale of height against width
  vec3 ll = normalize(vec3(uLDir.x, uLDir.y * vMeta.z, uLDir.z));
  float inside = sphereExit(vec3(vLoc.x, vLoc.y, vLoc.z), ll);
  float tauC = vMeta.w;
  float sunT = exp(-0.035 * tauC * inside);
  float vert = mix(1.0 - VERT_DARK, 1.0, pow(h, 0.7));
  vert = mix(vert, 1.0, vSheet * 0.8);                                   // and has no dark underside
  float beamT = pow(lam, 1.5) * mix(sunT, 1.0, 0.2) * mix(vert, 1.0, 0.2) * vShade;
  float ambF = (0.45 + 0.55 * mix(h, 1.0, 0.3)) * 0.8 * (0.8 + 0.2 * vert) * (0.7 + 0.3 * vShade);
  vec3 col = cloudColor(beamT, ambF, 1.0, d, dist);
  float a = cov * vMeta.x * (1.0 - exp(-0.35 * tauC)) * rangeFade(vWorld);
  // A cell has a flat base (the lifting condensation level), and the water in it stops there: a puff is a round sprite, so what hangs below the base
  // is cut off, with a ragged edge. Without this the sprites of the lowest puffs reach far under the base and the sea at the horizon cuts them.
  a *= smoothstep(0.0, 1.0, (vH + (n - 0.5) * 0.8 * vSoft) / vSoft);
  a *= smoothstep(20.0, 120.0, dist);                                   // do not smear over the lens close up
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #ifdef SYS_PASS
  // drawn into the bake (a linear target): encode as the screen would (the shader's colour is display referred, converted to linear for an sRGB screen
  // only so that the screen's own encoding gives it back), so that the bake holds what the direct draw would put on the screen and blends the same way
  if (uSrgb > 0.5) gl_FragColor.rgb = mix(gl_FragColor.rgb * 12.92, 1.055 * pow(max(gl_FragColor.rgb, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), gl_FragColor.rgb));
  #else
  #include <colorspace_fragment>
  #endif
}`;

// ------------------------------------------------------------------------------------------------------------------------------------
// Volume

const VOL_VERT = /* glsl */`
attribute vec4 aC;        // x, base, z, radius
attribute vec4 aD;        // thickness, alpha factor, seed, tau
varying vec3 vWorld; varying vec4 vC; varying vec4 vD;
void main() {
  vec3 w = vec3(aC.x + position.x * 2.8 * aC.w, aC.y + (position.y + 0.5) * aD.x * 1.25, aC.z + position.z * 2.8 * aC.w);
  vWorld = w; vC = aC; vD = aD;
  vec4 cp = projectionMatrix * viewMatrix * vec4(w, 1.0);
  gl_Position = vec4(cp.xy, cp.w * 0.9999, cp.w);
}`;

const VOL_FRAG = SHARED_GLSL + /* glsl */`
precision highp sampler3D;
uniform sampler3D uNoise;
uniform float uSteps;
varying vec3 vWorld; varying vec4 vC; varying vec4 vD;
float noiseAt(vec3 p, float seed) { return texture(uNoise, p + vec3(seed * 0.37, seed * 0.11, seed * 0.73)).r; }
// density of the cell at a point in its unit frame: a half ellipsoid with a flat base, eroded by billow noise toward the edge
float cellDensity(vec3 l, float seed, float lod) {
  float rho2 = dot(l.xz, l.xz);
  float hh = l.y;
  if (hh < -0.02 || hh > 1.12 || rho2 > 1.45) return 0.0;
  vec3 sp = vec3(seed * 0.37, seed * 0.11, seed * 0.73);
  float lobe = texture(uNoise, vec3(l.x * 0.5, l.y * 0.35, l.z * 0.5) + sp * 1.7).r;          // a few big lobes: the cell is not one dome
  float hb = hh - 0.07 * (lobe - 0.5);                                                          // and its base is not a ruled plane
  float f = rho2 * mix(0.8, 1.5, clamp(hb, 0.0, 1.0)) * (1.15 - 0.55 * lobe) + hb * hb * (0.75 + 0.5 * lobe);
  vec4 nz = texture(uNoise, l * 1.15 + sp);
  float billow = nz.g * 0.5 + nz.b * 0.32 + nz.a * 0.18;
  float e = f + 0.95 * (0.5 - billow) * lod + 0.3 * (0.5 - nz.r) * lod;
  float d = 1.0 - smoothstep(0.5, 1.02, e);
  return d * smoothstep(-0.02, 0.05, hb);
}
void main() {
  vec3 cam = cameraPosition;
  vec3 rd = normalize(vWorld - cam);
  float r = vC.w, thick = vD.x, seed = vD.z, tauC = vD.w;
  vec3 lo = vec3(vC.x - 1.4 * r, vC.y, vC.z - 1.4 * r), hi = vec3(vC.x + 1.4 * r, vC.y + thick * 1.25, vC.z + 1.4 * r);
  vec3 inv = 1.0 / rd;
  vec3 ta = (lo - cam) * inv, tb = (hi - cam) * inv;
  vec3 t0v = min(ta, tb), t1v = max(ta, tb);
  float t0 = max(max(max(t0v.x, t0v.y), t0v.z), 0.0), t1 = min(min(t1v.x, t1v.y), t1v.z);
  if (t1 <= t0) discard;
  float N = uSteps;
  float dt = (t1 - t0) / N;
  float jit = hash12(gl_FragCoord.xy + uFrame * 7.0);
  vec3 L = uLDir;
  vec3 lstep = vec3(L.x / r, L.y / thick, L.z / r);               // a unit of length toward the light, in the cell's frame
  float sigma = 0.55 * tauC / (0.5 * thick);                        // extinction per metre: the cell's optical depth over its mean column
  float T = 1.0, wsum = 0.0, beam = 0.0, ambA = 0.0, tmean = 0.0, vgM = 0.0;
  for (int i = 0; i < 64; i++) {
    if (float(i) >= N || T < 0.03) break;
    float t = t0 + (float(i) + jit) * dt;
    vec3 p = cam + rd * t;
    vec3 l = vec3((p.x - vC.x) / r, (p.y - vC.y) / thick, (p.z - vC.z) / r);
    float d = cellDensity(l, seed, 1.0);
    if (d < 0.004) continue;
    // toward the light: three taps, spaced out, the optical depth through the cell's own water (forward scattering keeps most of the beam)
    float tau = 0.0;
    for (int k = 1; k <= 3; k++) {
      float s = 0.11 * float(k * k);                              // 0.11, 0.44, 0.99 of a radius
      vec3 lp = l + vec3(L.x, L.y * (r / thick), L.z) * s;
      tau += cellDensity(lp, seed, 0.8) * (s - 0.11 * float((k - 1) * (k - 1)));
    }
    float beamT = exp(-0.2 * sigma * r * tau);
    float a = 1.0 - exp(-sigma * d * dt);
    float w = T * a;
    float hfrac = clamp(l.y, 0.0, 1.0);
    float vg = mix(1.0 - VERT_DARK, 1.0, pow(hfrac, 0.7));
    beam += w * beamT * vg; ambA += w * (0.45 + 0.55 * mix(hfrac, 1.0, 0.3)) * 0.8 * (0.8 + 0.2 * vg); vgM += w * vg;
    wsum += w; tmean += w * t;
    T *= 1.0 - a;
  }
  float alpha = 1.0 - T;
  if (alpha < 0.003) discard;
  float iw = 1.0 / max(wsum, 1e-6);
  tmean *= iw;
  vec3 pm = cam + rd * tmean;
  vec3 col = cloudColor(beam * iw, ambA * iw, 1.0, rd, tmean);
  gl_FragColor = vec4(col, clamp(alpha * vD.y * rangeFade(pm), 0.0, 1.0));
  #include <colorspace_fragment>
}`;

// ------------------------------------------------------------------------------------------------------------------------------------
// Rain curtains: one per raining cell, under its base, in both modes. A column of rain with a Gaussian section (its width a share of the cell's
// radius), leaning against the drift, marched in a box from the sea to the base. Grey, lit by the air under the cloud as the dome's curtains are.

const RAIN_VERT = /* glsl */`
attribute vec4 aR;        // x, z of the cell's centre, rain radius, base height
attribute vec4 aQ;        // rain mm/h, lean x, lean z (metres at the foot), presence
varying vec3 vWorld; varying vec4 vR; varying vec4 vQ;
void main() {
  float hw = 1.6 * aR.z + length(aQ.yz);
  vec3 w = vec3(aR.x - 0.5 * aQ.y + position.x * 2.0 * hw, (position.y + 0.5) * aR.w, aR.y - 0.5 * aQ.z + position.z * 2.0 * hw);
  vWorld = w; vR = aR; vQ = aQ;
  vec4 cp = projectionMatrix * viewMatrix * vec4(w, 1.0);
  gl_Position = vec4(cp.xy, cp.w * 0.9999, cp.w);
}`;

const RAIN_FRAG = SHARED_GLSL + /* glsl */`
uniform float uRainGain;
varying vec3 vWorld; varying vec4 vR; varying vec4 vQ;
void main() {
  vec3 cam = cameraPosition;
  vec3 rd = normalize(vWorld - cam);
  float R = vR.z, base = vR.w;
  vec2 lean = vQ.yz;
  float hw = 1.6 * R + length(lean);
  vec3 lo = vec3(vR.x - 0.5 * lean.x - hw, 0.0, vR.y - 0.5 * lean.y - hw), hi = vec3(vR.x - 0.5 * lean.x + hw, base, vR.y - 0.5 * lean.y + hw);
  vec3 inv = 1.0 / rd;
  vec3 ta = (lo - cam) * inv, tb = (hi - cam) * inv;
  vec3 t0v = min(ta, tb), t1v = max(ta, tb);
  float t0 = max(max(max(t0v.x, t0v.y), t0v.z), 0.0), t1 = min(min(t1v.x, t1v.y), t1v.z);
  if (t1 <= t0) discard;
  const int N = 14;
  float dt = (t1 - t0) / float(N);
  float jit = hash12(gl_FragCoord.xy + uFrame * 5.0);
  float sig2 = 2.0 * (0.5 * R) * (0.5 * R);
  float peak = 2.3 * vQ.x;                                       // the mean over the core's disc is 0.43 of the peak of a Gaussian of sigma = half the radius
  float tau = 0.0, tw = 0.0;
  for (int i = 0; i < N; i++) {
    float t = t0 + (float(i) + jit) * dt;
    vec3 p = cam + rd * t;
    float h = clamp(p.y / base, 0.0, 1.0);
    vec2 ax = vR.xy - lean * (1.0 - h);
    vec2 q = p.xz - ax;
    float prof = exp(-dot(q, q) / sig2);
    float Rl = max(peak * prof - 0.25, 0.0);
    if (Rl < 0.01) continue;
    float st = 0.65 + 0.7 * vnoise(vec3(p.xz * 0.03, vR.x * 0.001));         // streaks
    float top = 0.35 + 0.65 * smoothstep(0.0, 0.1, 1.0 - h);               // the rain thickens out of the base
    float dtau = uRainGain * ${RAIN_K.toFixed(4)} * pow(Rl, 0.9) * st * top * dt;
    tau += dtau; tw += dtau * t;
  }
  if (tau < 0.002) discard;
  float dist = tw / tau;
  vec3 pm = cam + rd * dist;
  vec3 lit = whiten(uSrc * cloudBeam(rd, dist)) + uMoonSrc;
  float fwd = pow(max(dot(rd, uLDir), 0.0), 8.0);
  vec3 direct = lit * (0.08 + 0.55 * fwd);
  vec3 amb = uAmb * ambField(rd, 0.5) * 0.7 * (1.0 + NIGHT_AMB * uNight);
  vec3 cdt = tone(scoto(direct) * uCgain.x + scoto(amb) * uGain, uHpCloud);
  cdt = mix(cdt, skyTone(rd), 0.9 * (1.0 - exp(-dist / HAZE_RANGE)));
  vec3 col = mix(cdt, srgbToLinear(cdt), uSrgb * (1.0 - smoothstep(0.35, 1.0, uNight)));
  gl_FragColor = vec4(col, clamp((1.0 - exp(-tau)) * vQ.w * rangeFade(vec3(vR.x, 0.0, vR.y)), 0.0, 0.9));
  #include <colorspace_fragment>
}`;

// ------------------------------------------------------------------------------------------------------------------------------------
// The baked far sky: one pass that looks up the picture of the far clouds in the six-face atlas

/** The faces of the baked sky: forward and up (right = forward x up, so each face is a right handed camera: right, up, back). Shared by the bake and the lookup. */
const FACE_A = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
const FACE_U = [[0, 1, 0], [0, 1, 0], [0, 0, 1], [0, 0, -1], [0, 1, 0], [0, 1, 0]];
const FACE_R = FACE_A.map((a, i) => { const u = FACE_U[i]; return [a[1] * u[2] - a[2] * u[1], a[2] * u[0] - a[0] * u[2], a[0] * u[1] - a[1] * u[0]]; });
const faceTable = (name: string, t: number[][]) => `const vec3 ${name}[6] = vec3[6](${t.map(v => `vec3(${v.map(x => x.toFixed(1)).join(', ')})`).join(', ')});`;

const BAKE_VERT = /* glsl */`
varying vec3 vDir;
void main() {
  vec3 vd = vec3(position.x / projectionMatrix[0][0], position.y / projectionMatrix[1][1], -1.0);
  vDir = vd * mat3(viewMatrix);                                  // the view rotation's transpose: the direction in the world
  gl_Position = vec4(position.xy, 0.9999, 1.0);                  // on the far plane, as the cells are: the scene's depth decides what hides it
}`;

const BAKE_FRAG = /* glsl */`
precision highp float;
uniform sampler2D uA;
uniform sampler2D uB;
uniform float uWa[6];
uniform float uWb[6];
uniform float uInvT;                                              // 1 / tan(half the faces' field of view)
varying vec3 vDir;
${faceTable('FA', FACE_A)}
${faceTable('FR', FACE_R)}
${faceTable('FU', FACE_U)}
// the atlas value in direction d, from face f, from whichever of the two atlases hold it (blended by their weights)
vec4 fetchFace(vec3 d, int f) {
  float dz = dot(d, FA[f]);
  vec2 t = vec2(dot(d, FR[f]), dot(d, FU[f])) / dz;
  vec2 uv = (vec2(float(f - 3 * (f / 3)), float(f / 3)) + t * uInvT * 0.5 + 0.5) / vec2(3.0, 2.0);
  float wa = uWa[f], wb = uWb[f];
  vec4 c = vec4(0.0);
  if (wa > 0.0) c += texture2D(uA, uv) * wa;
  if (wb > 0.0) c += texture2D(uB, uv) * wb;
  return c;
}
void main() {
  vec3 d = normalize(vDir);
  if (d.y < -0.03) discard;                                       // the far clouds are all above the horizon
  vec3 ad = abs(d);
  int i1 = ad.x >= ad.y && ad.x >= ad.z ? 0 : (ad.y >= ad.z ? 1 : 2);
  float m1 = ad[i1];
  vec3 o = ad; o[i1] = -1.0;
  int i2 = o.x >= o.y && o.x >= o.z ? 0 : (o.y >= o.z ? 1 : 2);
  float m2 = o[i2];
  int f1 = 2 * i1 + (d[i1] > 0.0 ? 0 : 1);
  vec4 c = fetchFace(d, f1);
  // near a face's edge the two faces were drawn at different times, so they differ by a few pixels: blend them over the margin the faces share
  float w2 = 0.5 * smoothstep(uInvT, 1.0, m2 / m1);
  if (w2 > 0.0) c = mix(c, fetchFace(d, 2 * i2 + (d[i2] > 0.0 ? 0 : 1)), w2);
  if (c.a < 0.003) discard;
  gl_FragColor = c;                                               // premultiplied, already encoded as the screen wants it
}`;

/** The far-to-near order of n keys (a larger key is nearer), by a counting sort on the key's top 12 bits; `ord` receives the indices. */
function farToNear(keys: Uint16Array, n: number, ord: Uint32Array) {
  const nb = 4096, cnt = new Uint32Array(nb + 1);
  for (let i = 0; i < n; i++) cnt[(keys[i] >> 4) + 1]++;
  for (let b = 0; b < nb; b++) cnt[b + 1] += cnt[b];
  for (let i = 0; i < n; i++) ord[cnt[keys[i] >> 4]++] = i;
}

/** Reorders a buffer of n items of `stride` floats by `ord` (item i of the result is item ord[i] of the input), through `tmp`. */
function permute(arr: Float32Array, ord: Uint32Array, n: number, stride: number, tmp: Float32Array) {
  for (let i = 0; i < n; i++) { const s = ord[i] * stride, d = i * stride; for (let k = 0; k < stride; k++) tmp[d + k] = arr[s + k]; }
  arr.set(tmp.subarray(0, n * stride));
}

/** The numbers of a uniform's value (a number, a vector or a colour), for telling whether the light or the exposure moved. */
function uniformNums(v: any, out: number[]) {
  if (typeof v === 'number') out.push(v);
  else if (v && typeof v === 'object') { if ('x' in v) { out.push(v.x); if ('y' in v) out.push(v.y); if ('z' in v) out.push(v.z); } else if ('r' in v) out.push(v.r, v.g, v.b); }
}

/** A 3D noise tile (periodic): r a smooth value-noise fbm, g b a three periodic Worley (cellular) noises at rising frequency, as billows. */
function makeNoise3D(n = 48): THREE.Data3DTexture {
  const rnd = mulberry32(12345);
  const data = new Uint8Array(n * n * n * 4);
  const lattice = (f: number) => { const a = new Float32Array(f * f * f); for (let i = 0; i < a.length; i++) a[i] = rnd(); return a; };
  const value = (a: Float32Array, f: number, x: number, y: number, z: number) => {
    const fx = x * f, fy = y * f, fz = z * f, ix = Math.floor(fx), iy = Math.floor(fy), iz = Math.floor(fz);
    const ux = fx - ix, uy = fy - iy, uz = fz - iz;
    const sx = ux * ux * (3 - 2 * ux), sy = uy * uy * (3 - 2 * uy), sz = uz * uz * (3 - 2 * uz);
    const g = (i: number, j: number, k: number) => a[(((k % f) + f) % f * f + ((j % f) + f) % f) * f + ((i % f) + f) % f];
    const x00 = g(ix, iy, iz) * (1 - sx) + g(ix + 1, iy, iz) * sx, x10 = g(ix, iy + 1, iz) * (1 - sx) + g(ix + 1, iy + 1, iz) * sx;
    const x01 = g(ix, iy, iz + 1) * (1 - sx) + g(ix + 1, iy, iz + 1) * sx, x11 = g(ix, iy + 1, iz + 1) * (1 - sx) + g(ix + 1, iy + 1, iz + 1) * sx;
    return (x00 * (1 - sy) + x10 * sy) * (1 - sz) + (x01 * (1 - sy) + x11 * sy) * sz;
  };
  const worleyPts = (f: number) => { const p = new Float32Array(f * f * f * 3); for (let i = 0; i < p.length; i++) p[i] = rnd(); return p; };
  const worley = (p: Float32Array, f: number, x: number, y: number, z: number) => {
    const cx = Math.floor(x * f), cy = Math.floor(y * f), cz = Math.floor(z * f);
    let best = 9;
    for (let dz = -1; dz <= 1; dz++) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const ix = cx + dx, iy = cy + dy, iz = cz + dz;
      const wx = ((ix % f) + f) % f, wy = ((iy % f) + f) % f, wz = ((iz % f) + f) % f;
      const o = ((wz * f + wy) * f + wx) * 3;
      const px = ix + p[o], py = iy + p[o + 1], pz = iz + p[o + 2];
      const ddx = px - x * f, ddy = py - y * f, ddz = pz - z * f;
      best = Math.min(best, ddx * ddx + ddy * ddy + ddz * ddz);
    }
    return 1 - Math.min(1, Math.sqrt(best));
  };
  const v1 = lattice(3), v2 = lattice(6), v3 = lattice(12);
  const w1 = worleyPts(3), w2 = worleyPts(6), w3 = worleyPts(12);
  for (let k = 0; k < n; k++) for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    const x = (i + 0.5) / n, y = (j + 0.5) / n, z = (k + 0.5) / n;
    const o = ((k * n + j) * n + i) * 4;
    const r = 0.55 * value(v1, 3, x, y, z) + 0.3 * value(v2, 6, x, y, z) + 0.15 * value(v3, 12, x, y, z);
    data[o] = Math.round(255 * Math.min(1, Math.max(0, (r - 0.5) * 1.8 + 0.5)));
    data[o + 1] = Math.round(255 * worley(w1, 3, x, y, z)); data[o + 2] = Math.round(255 * worley(w2, 6, x, y, z)); data[o + 3] = Math.round(255 * worley(w3, 12, x, y, z));
  }
  const tex = new THREE.Data3DTexture(data, n, n, n);
  tex.format = THREE.RGBAFormat; tex.type = THREE.UnsignedByteType;
  tex.minFilter = tex.magFilter = THREE.LinearFilter; tex.wrapS = tex.wrapT = tex.wrapR = THREE.RepeatWrapping;
  tex.unpackAlignment = 1; tex.needsUpdate = true;
  return tex;
}

// ------------------------------------------------------------------------------------------------------------------------------------

interface Vis { c: Cell; px: number; pz: number; dist: number; ground: number; thick: number; r: number; base: number; vol: boolean; k: number; shade: number; baked: boolean }

/** One face of the baked sky: what it holds, how it is fading in, when and from where it was drawn. */
interface Face {
  has: boolean; cur: number;               // it holds a picture; in which atlas (0 or 1) the newest one is
  prevHas: boolean; fade: number;          // an older picture is in the other atlas while the newest fades in (fade 0 to 1)
  t: number; cam: THREE.Vector3;           // clock and eye position when it was drawn
  sig: number[]; empty: boolean;           // the light and exposure it was drawn under; drawn with no cloud in it
}

const LIGHT_KEYS = ['uGain', 'uCgain', 'uSrc', 'uAmb', 'uNight', 'uFlux', 'uFloor', 'uSVScale', 'uElH', 'uEyeH', 'uLDir', 'uWB', 'uMoonSrc', 'uHpSky', 'uHpCloud'];

export class CloudCells {
  readonly group = new THREE.Group();
  private store = new CellStore();
  private mode: CellMode = 'hybrid';
  private puffMesh: THREE.Mesh; private volMesh: THREE.Mesh;
  private puffGeo = new THREE.InstancedBufferGeometry(); private volGeo = new THREE.InstancedBufferGeometry();
  private aPos = new THREE.InstancedBufferAttribute(new Float32Array(PUFF_CAP * 4), 4);
  private aLoc = new THREE.InstancedBufferAttribute(new Float32Array(PUFF_CAP * 4), 4);
  private aMeta = new THREE.InstancedBufferAttribute(new Float32Array(PUFF_CAP * 4), 4);
  private aShade = new THREE.InstancedBufferAttribute(new Float32Array(PUFF_CAP), 1);
  private aBase = new THREE.InstancedBufferAttribute(new Float32Array(PUFF_CAP * 2), 2);
  private lightDir: THREE.Vector3;
  private aC = new THREE.InstancedBufferAttribute(new Float32Array(VOL_CAP * 4), 4);
  private aD = new THREE.InstancedBufferAttribute(new Float32Array(VOL_CAP * 4), 4);
  private uSteps = { value: 24 };
  private uRainGain = { value: RAIN_GAIN };
  private uRange = { value: new THREE.Vector2(CELL_RANGE_NEAR, CELL_RANGE_FAR) };
  private cullCam = new THREE.PerspectiveCamera();
  private frustum = new THREE.Frustum(); private pv = new THREE.Matrix4(); private sph = new THREE.Sphere();
  private vis: Vis[] = [];
  private keys = new Uint16Array(PUFF_CAP); private tmpOrder = new Uint32Array(PUFF_CAP);
  private stats = { cells: 0, puffs: 0, vols: 0, rain: 0, ms: 0 };
  private noise: THREE.Data3DTexture;
  private rainGeo = new THREE.InstancedBufferGeometry(); private rainMesh: THREE.Mesh;
  private aR = new THREE.InstancedBufferAttribute(new Float32Array(RAIN_CAP * 4), 4);
  private aQ = new THREE.InstancedBufferAttribute(new Float32Array(RAIN_CAP * 4), 4);
  /** Cells nearer than this (ground distance) are volumes in hybrid mode. */
  volumeRange = VOLUME_RANGE;
  /** Cells at the most this many volumes in hybrid mode. */
  volumeMax = VOLUME_MAX;

  // The far-field bake
  private shared: Record<string, THREE.IUniform>;
  private clock = 0; private frameNo = 0; private bakeFrame = -1; private mainCam: THREE.Camera | null = null;
  private bakeAllowed = true; private bakeOn = false;
  private atlas: THREE.WebGLRenderTarget[] = [];
  private bakeScene = new THREE.Scene(); private bakeCam = new THREE.PerspectiveCamera(BAKE_FOV, 1, 1, 1e6);
  private bakeGeo = new THREE.InstancedBufferGeometry(); private bakeMesh: THREE.Mesh;
  private bP = new THREE.InstancedBufferAttribute(new Float32Array(BAKE_PUFF_CAP * 4), 4);
  private bL = new THREE.InstancedBufferAttribute(new Float32Array(BAKE_PUFF_CAP * 4), 4);
  private bM = new THREE.InstancedBufferAttribute(new Float32Array(BAKE_PUFF_CAP * 4), 4);
  private bS = new THREE.InstancedBufferAttribute(new Float32Array(BAKE_PUFF_CAP), 1);
  private bB = new THREE.InstancedBufferAttribute(new Float32Array(BAKE_PUFF_CAP * 2), 2);
  private bKeys = new Uint16Array(BAKE_PUFF_CAP); private bOrd = new Uint32Array(BAKE_PUFF_CAP); private bTmp = new Float32Array(BAKE_PUFF_CAP * 4);
  private showMesh: THREE.Mesh; private hookMesh: THREE.Mesh;
  private wa = new Float32Array(6); private wb = new Float32Array(6);
  private faces: Face[] = Array.from({ length: 6 }, () => ({ has: false, cur: 0, prevHas: false, fade: 1, t: -1e9, cam: new THREE.Vector3(), sig: [], empty: false }));
  private faceCells = new Int32Array(6); private faceMinG = new Float32Array(6); private faceVmax = new Float32Array(6);
  private faceFading = new Uint8Array(6); private faceForce = new Uint8Array(6);
  private faceSamples: THREE.Vector3[][] = [];
  private todo: number[] = [];
  private lightNow: number[] = [];
  private farList: Vis[] = []; private farBuiltAt = -1e9;
  private pxPerRad = 800;
  private bakeStats = { far: 0, faces: 0, redraws: 0, lastMs: 0, puffs: 0 };
  private tmpS = new THREE.Vector2(); private tmpV = new THREE.Vector3(); private tmpM = new THREE.Matrix4(); private tmpR = new THREE.Vector3(); private tmpU = new THREE.Vector3(); private tmpB = new THREE.Vector3();

  constructor(dome: Sky) {
    this.noise = makeNoise3D();
    const shared = dome.cellUniforms();
    this.shared = shared;
    this.lightDir = shared.uLDir.value as THREE.Vector3;
    const quad = new THREE.PlaneGeometry(2, 2);
    this.puffGeo.index = quad.index; this.puffGeo.setAttribute('position', quad.getAttribute('position'));
    this.puffGeo.setAttribute('aPos', this.aPos); this.puffGeo.setAttribute('aLoc', this.aLoc); this.puffGeo.setAttribute('aMeta', this.aMeta); this.puffGeo.setAttribute('aShade', this.aShade); this.puffGeo.setAttribute('aBase', this.aBase);
    for (const a of [this.aPos, this.aLoc, this.aMeta, this.aShade, this.aBase, this.aC, this.aD, this.bP, this.bL, this.bM, this.bS, this.bB]) a.setUsage(THREE.DynamicDrawUsage);
    this.puffGeo.instanceCount = 0;
    const box = new THREE.BoxGeometry(1, 1, 1);
    this.volGeo.index = box.index; this.volGeo.setAttribute('position', box.getAttribute('position'));
    this.volGeo.setAttribute('aC', this.aC); this.volGeo.setAttribute('aD', this.aD);
    this.volGeo.instanceCount = 0;
    this.rainGeo.index = box.index; this.rainGeo.setAttribute('position', box.getAttribute('position'));
    this.rainGeo.setAttribute('aR', this.aR); this.rainGeo.setAttribute('aQ', this.aQ);
    this.aR.setUsage(THREE.DynamicDrawUsage); this.aQ.setUsage(THREE.DynamicDrawUsage);
    this.rainGeo.instanceCount = 0;
    const common = { transparent: true, depthTest: true, depthWrite: false, fog: false, toneMapped: false };
    this.puffMesh = new THREE.Mesh(this.puffGeo, new THREE.ShaderMaterial({
      vertexShader: PUFF_VERT, fragmentShader: PUFF_FRAG, uniforms: { ...shared, uRange: this.uRange }, side: THREE.DoubleSide, ...common }));
    this.volMesh = new THREE.Mesh(this.volGeo, new THREE.ShaderMaterial({
      vertexShader: VOL_VERT, fragmentShader: VOL_FRAG, uniforms: { ...shared, uRange: this.uRange, uNoise: { value: this.noise }, uSteps: this.uSteps }, side: THREE.BackSide, ...common }));
    this.rainMesh = new THREE.Mesh(this.rainGeo, new THREE.ShaderMaterial({
      vertexShader: RAIN_VERT, fragmentShader: RAIN_FRAG, uniforms: { ...shared, uRange: this.uRange, uRainGain: this.uRainGain }, side: THREE.BackSide, ...common }));
    this.rainMesh.name = 'cloud cells (rain)';
    for (const m of [this.puffMesh, this.volMesh, this.rainMesh]) { m.frustumCulled = false; m.renderOrder = 10; }
    this.rainMesh.renderOrder = 11;                              // after the cloud: the curtain hangs under the base, in front of what is behind it
    this.puffMesh.name = 'cloud cells (puffs)'; this.volMesh.name = 'cloud cells (volumes)';

    // the bake: the far puffs drawn with the same shader into a 3x2 atlas of faces, two atlases so that a redrawn face can be faded in
    this.bakeGeo.index = quad.index; this.bakeGeo.setAttribute('position', quad.getAttribute('position'));
    this.bakeGeo.setAttribute('aPos', this.bP); this.bakeGeo.setAttribute('aLoc', this.bL); this.bakeGeo.setAttribute('aMeta', this.bM); this.bakeGeo.setAttribute('aShade', this.bS); this.bakeGeo.setAttribute('aBase', this.bB);
    this.bakeGeo.instanceCount = 0;
    this.bakeMesh = new THREE.Mesh(this.bakeGeo, new THREE.ShaderMaterial({
      vertexShader: PUFF_VERT, fragmentShader: PUFF_FRAG, defines: { SYS_PASS: '' }, uniforms: { ...shared, uRange: this.uRange },
      side: THREE.DoubleSide, transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false }));
    this.bakeMesh.frustumCulled = false; this.bakeScene.add(this.bakeMesh);
    for (let i = 0; i < 2; i++) this.atlas.push(new THREE.WebGLRenderTarget(3 * BAKE_FACE, 2 * BAKE_FACE, {
      type: THREE.UnsignedByteType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false }));
    this.showMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
      vertexShader: BAKE_VERT, fragmentShader: BAKE_FRAG,
      uniforms: { uA: { value: this.atlas[0].texture }, uB: { value: this.atlas[1].texture }, uWa: { value: this.wa }, uWb: { value: this.wb }, uInvT: { value: 1 / Math.tan(BAKE_FOV * Math.PI / 360) } },
      side: THREE.DoubleSide, transparent: true, premultipliedAlpha: true, depthTest: true, depthWrite: false, fog: false, toneMapped: false }));
    this.showMesh.frustumCulled = false; this.showMesh.renderOrder = 9; this.showMesh.visible = false; this.showMesh.name = 'cloud cells (baked far sky)';
    // a mesh that draws nothing and runs first, so that the bake is redrawn inside the frame's own render, with the frame's own camera and renderer
    const hg = new THREE.BufferGeometry(); hg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
    this.hookMesh = new THREE.Mesh(hg, new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, depthTest: false, colorWrite: false }));
    this.hookMesh.frustumCulled = false; this.hookMesh.renderOrder = 8; this.hookMesh.name = 'cloud cells (bake)';
    this.hookMesh.onBeforeRender = (renderer, _scene, camera) => { if (camera === this.mainCam) this.prepare(renderer as THREE.WebGLRenderer, camera as THREE.PerspectiveCamera); };
    for (let f = 0; f < 6; f++) {
      const a = new THREE.Vector3(...(FACE_A[f] as [number, number, number])), r = new THREE.Vector3(...(FACE_R[f] as [number, number, number])), u = new THREE.Vector3(...(FACE_U[f] as [number, number, number]));
      const s: THREE.Vector3[] = [];
      for (let i = -2; i <= 2; i++) for (let j = -2; j <= 2; j++) s.push(a.clone().addScaledVector(r, i * 0.5).addScaledVector(u, j * 0.5).normalize());
      this.faceSamples.push(s);
    }
    this.group.add(this.hookMesh, this.showMesh, this.puffMesh, this.volMesh, this.rainMesh);
    this.group.name = 'cloud cells';
  }

  setMode(mode: CellMode) { this.mode = mode; }
  getMode(): CellMode { return this.mode; }
  /** Switch the far-field bake off (every cell drawn directly, as in mode 'direct') or on. */
  setBake(on: boolean) { this.bakeAllowed = on; }
  /** Steps through a volume (8 to 64): the volume renderer's cost knob. */
  setVolumeSteps(n: number) { this.uSteps.value = Math.max(8, Math.min(64, Math.round(n))); }
  /** The raining discs as last laid out (the nearest first): packs x, z, radius, mm/h (eased in with the cell's presence) into `out`, 4 floats each, up to `max` discs. Returns the count. Three-axis metres. */
  rainDiscs(out: Float32Array, max: number): number {
    const A = this.aR.array as Float32Array, B = this.aQ.array as Float32Array, nr = this.rainGeo.instanceCount;
    let n = 0;
    for (let k = nr - 1; k >= 0 && n < max; k--, n++) {           // the curtains are written far to near
      out[n * 4] = A[k * 4]; out[n * 4 + 1] = A[k * 4 + 1]; out[n * 4 + 2] = A[k * 4 + 2]; out[n * 4 + 3] = B[k * 4] * B[k * 4 + 3];
    }
    return n;
  }

  /** A multiplier on the rain curtains' extinction (1 = the dome's 1 per km at 1 mm/h; the default is RAIN_GAIN), to look at light showers. */
  setRainGain(g: number) { this.uRainGain.value = Math.max(0, Math.min(20, g)); }
  ingest(msg: CloudCellsMessage) { this.store.ingest(msg); }
  /** True while cells are being drawn: the dome then leaves out its own low layer near the eye. */
  get active() { return this.mode !== 'off' && this.store.cells.size > 0; }

  describe(): string {
    const s = this.stats, b = this.bakeStats;
    const bk = this.bakeOn ? `; far sky baked: ${b.far} cells, ${b.puffs} puffs in ${b.faces} faces, ${b.redraws} face redraws so far (last frame's ${b.lastMs.toFixed(2)} ms)` : '; no bake';
    return `cloud cells: mode ${this.mode}, ${this.store.cells.size} cells held, drawn ${s.cells} (${s.puffs} puffs, ${s.vols} volumes, ${this.uSteps.value} steps, ${s.rain} rain curtains), update ${s.ms.toFixed(2)} ms${bk}`;
  }

  update(camera: THREE.PerspectiveCamera, dt: number) {
    const t0 = performance.now();
    const on = this.active;
    this.group.visible = on;
    if (!on) {
      this.puffGeo.instanceCount = 0; this.volGeo.instanceCount = 0; this.rainGeo.instanceCount = 0; this.showMesh.visible = false; this.bakeOn = false; this.todo.length = 0;
      this.stats = { cells: 0, puffs: 0, vols: 0, rain: 0, ms: 0 }; return;
    }
    this.frameNo++; this.clock += dt; this.mainCam = camera;
    this.store.update(dt);
    const bake = this.bakeAllowed && this.mode === 'puffs';
    if (bake !== this.bakeOn) {                                   // switched: whatever the faces hold is out of date
      this.bakeOn = bake;
      for (const F of this.faces) { F.has = false; F.prevHas = false; F.fade = 1; F.empty = false; }
    }

    // a frustum with a far plane out past the cells' range (the camera's own is a few km); a little wider than the view, for the bake's choice of faces
    const cc = this.cullCam;
    cc.fov = Math.min(140, camera.fov * 1.12); cc.aspect = camera.aspect; cc.near = 1; cc.far = 4e4; cc.updateProjectionMatrix();
    cc.position.copy(camera.position); cc.quaternion.copy(camera.quaternion); cc.updateMatrixWorld(true);
    this.pv.multiplyMatrices(cc.projectionMatrix, cc.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.pv);

    const T = this.store.tile, cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
    const vis = this.vis; vis.length = 0;
    this.faceMinG.fill(1e9); this.faceVmax.fill(0); this.faceCells.fill(0); this.faceFading.fill(0);
    let farCells = 0;
    for (const c of this.store.cells.values()) {
      if (c.pres <= 0.01) continue;
      const [x, z] = this.store.position(c);
      const grow = Math.pow(c.pres, 0.7);
      const r = Math.max(60, c.r * grow), thick = Math.max(80, (c.top - c.base) * grow);
      // the periodic tile: the nearest image and any other within range
      const bx = x - Math.round((x - cx) / T) * T, bz = z - Math.round((z - cz) / T) * T;
      const mid = c.base + thick * 0.5;
      if (bake) this.classify(c, Math.hypot(bx - cx, bz - cz), bx - cx, mid - cy, bz - cz); else c.zone = ZONE_NEAR;
      const baked = bake && c.zone === ZONE_FAR, inBake = bake && (c.zone === ZONE_FAR || c.zone === ZONE_ENTERING);
      if (inBake) farCells++;
      for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
        const px = bx + i * T, pz = bz + j * T;
        const ground = Math.hypot(px - cx, pz - cz);
        if (ground - 1.4 * r > CELL_RANGE_FAR) continue;
        if (inBake) this.noteFar(c, px - cx, mid - cy, pz - cz, ground);
        this.sph.center.set(px, mid, pz); this.sph.radius = Math.max(1.5 * r, thick);
        if (!this.frustum.intersectsSphere(this.sph)) continue;
        const dist = Math.hypot(px - cx, mid - cy, pz - cz);
        vis.push({ c, px, pz, dist, ground, thick, r, base: c.base, vol: false, k: grow, shade: 1, baked });
      }
    }
    this.shadeCells(vis, vis);
    // which are volumes
    if (this.mode === 'volume') for (const v of vis) v.vol = !v.c.hi;                       // outflow cells are thin sheets: puffs in every mode
    else if (this.mode === 'hybrid') {
      const near = vis.filter(v => !v.c.hi && v.ground < this.volumeRange).sort((a, b) => a.ground - b.ground);
      for (let i = 0; i < Math.min(near.length, this.volumeMax); i++) near[i].vol = true;
    }
    this.planBake(camera, dt);
    this.bakeStats.far = farCells;

    // puffs: write all instances (the baked cells' are in the bake), then order far to near (a counting sort on a 16 bit distance key)
    let np = 0;
    const P = this.aPos.array as Float32Array, Lc = this.aLoc.array as Float32Array, M = this.aMeta.array as Float32Array, SH = this.aShade.array as Float32Array, BS = this.aBase.array as Float32Array;
    for (const v of vis) {
      if (v.vol || v.baked) continue;
      np = this.emitPuffs(v, np, PUFF_CAP, P, Lc, M, SH, BS, this.keys, cx, cy, cz);
    }
    const ord = this.tmpOrder, tmp = this.tmp;
    farToNear(this.keys, np, ord);
    permute(P, ord, np, 4, tmp); permute(Lc, ord, np, 4, tmp); permute(M, ord, np, 4, tmp); permute(SH, ord, np, 1, tmp); permute(BS, ord, np, 2, tmp);
    this.puffGeo.instanceCount = np;
    this.aPos.needsUpdate = this.aLoc.needsUpdate = this.aMeta.needsUpdate = this.aShade.needsUpdate = this.aBase.needsUpdate = true;

    // volumes, far to near
    const vols = vis.filter(v => v.vol).sort((a, b) => b.dist - a.dist);
    const C = this.aC.array as Float32Array, D = this.aD.array as Float32Array;
    let nv = 0;
    for (const v of vols) {
      if (nv >= VOL_CAP) break;
      const o = nv * 4;
      C[o] = v.px; C[o + 1] = v.base; C[o + 2] = v.pz; C[o + 3] = v.r;
      D[o] = v.thick; D[o + 1] = v.c.pres; D[o + 2] = (v.c.seed % 97) * 0.173; D[o + 3] = Math.max(2, v.c.lwp * cloudOptics.tauPerGram());
      nv++;
    }
    this.volGeo.instanceCount = nv;
    this.aC.needsUpdate = this.aD.needsUpdate = true;

    // rain curtains, far to near
    const wet = vis.filter(v => v.c.rain * v.c.pres >= RAIN_MIN && v.c.rr > 30 && v.ground - 2 * v.r < CELL_RANGE_FAR).sort((a, b) => b.dist - a.dist);
    const A = this.aR.array as Float32Array, B = this.aQ.array as Float32Array;
    let nr = 0;
    for (const v of wet) {
      if (nr >= RAIN_CAP) break;
      const c = v.c, o = nr * 4;
      const sp = Math.hypot(c.vx, c.vz) || 1;
      A[o] = v.px + c.rx; A[o + 1] = v.pz + c.rz; A[o + 2] = Math.max(60, c.rr * Math.pow(c.pres, 0.7)); A[o + 3] = v.base;
      B[o] = c.rain; B[o + 1] = c.vx / sp * RAIN_LEAN_M; B[o + 2] = c.vz / sp * RAIN_LEAN_M; B[o + 3] = Math.pow(c.pres, 1.5);
      nr++;
    }
    this.rainGeo.instanceCount = nr;
    this.aR.needsUpdate = this.aQ.needsUpdate = true;
    this.stats = { cells: vis.length, puffs: np, vols: nv, rain: nr, ms: performance.now() - t0 };
  }

  /** Writes one cell's puffs into the buffers from index np on and returns the new count. The keys are the distance from the eye, for the far-to-near order. */
  private emitPuffs(v: Vis, np: number, cap: number, P: Float32Array, Lc: Float32Array, M: Float32Array, SH: Float32Array, BS: Float32Array, keys: Uint16Array, cx: number, cy: number, cz: number): number {
    const c = v.c, maxD = CELL_RANGE_FAR + 3000;
    if (!c.puffs) c.puffs = c.hi ? sheetLayout(c.seed, c.r) : puffLayout(c.seed, c.r);
    const L = c.puffs, n = L.length / PS;
    const A0 = Math.PI * v.r * v.r, sf = Math.max(0, Math.min(1, Math.log10(Math.max(c.sa, A0) / A0) / 1.5));         // 0 for a cell alone, 1 in a system 30 times its size
    const bodyScale = 1 + 0.5 * sf;
    const tau = Math.max(2, c.lwp * cloudOptics.tauPerGram()), aspect = v.r / v.thick;
    const lodScale = v.dist > 9000 ? 1.35 : 1.0;                            // far cells use broader puffs
    const sizeBase = Math.sqrt(v.r * v.thick);
    for (let i = 0; i < n; i++) {
      if (np >= cap) break;
      const nx = L[i * PS], nh = L[i * PS + 1], nz = L[i * PS + 2], ns = L[i * PS + 3], kind = L[i * PS + 4];
      if (lodScale > 1 && (i & 1) && nh > 0.2 && kind < 0.5) continue;
      const wx = v.px + nx * v.r, wy = v.base + nh * v.thick, wz = v.pz + nz * v.r;
      const o = np * 4;
      P[o] = wx; P[o + 1] = wy; P[o + 2] = wz; P[o + 3] = ns * (0.55 * v.r + 0.45 * sizeBase) * 1.5 * lodScale * (c.hi ? 0.8 : 1.0) * (kind > 0.5 ? bodyScale : 1.0);
      Lc[o] = nx; Lc[o + 1] = nh; Lc[o + 2] = nz; Lc[o + 3] = c.hi ? 1 : 0;
      M[o] = (c.hi ? 0.5 : 0.82) * c.pres; M[o + 1] = ((c.seed * 0.618 + i * 0.37) % 7.0); M[o + 2] = aspect; M[o + 3] = tau;
      SH[np] = v.shade;
      BS[np * 2] = v.base; BS[np * 2 + 1] = Math.max(12, 0.05 * v.thick);
      const dd = Math.hypot(wx - cx, wy - cy, wz - cz);
      keys[np] = Math.min(65535, Math.round(65535 * (1 - Math.min(1, dd / maxD))));      // far first: a larger key is nearer, so sort by descending 1 - d
      np++;
    }
    return np;
  }

  // ----------------------------------------------------------------------------------------------------------------------------------
  // The far-field bake

  /** Moves a cell between the zones with hysteresis: near (drawn directly), far (baked), and the two crossings, in which it is drawn both ways for a while. */
  private classify(c: Cell, g: number, dx: number, dy: number, dz: number) {
    switch (c.zone) {
      case -1: c.zone = g > FAR_NEAR_M ? ZONE_FAR : ZONE_NEAR; c.zt = this.clock; if (c.zone === ZONE_FAR) this.markFaces(dx, dy, dz); break;
      case ZONE_NEAR: if (g > FAR_NEAR_M + FAR_HYST_M) { c.zone = ZONE_ENTERING; c.zt = this.clock; this.markFaces(dx, dy, dz); } break;
      case ZONE_ENTERING: if (g < FAR_NEAR_M) { c.zone = ZONE_NEAR; this.markFaces(dx, dy, dz); } else if (this.clock - c.zt > BAKE_HOLD_S) c.zone = ZONE_FAR; break;
      case ZONE_FAR: if (g < FAR_NEAR_M - FAR_HYST_M) { c.zone = ZONE_LEAVING; c.zt = this.clock; this.markFaces(dx, dy, dz); } break;
      case ZONE_LEAVING: if (g > FAR_NEAR_M) { c.zone = ZONE_FAR; this.markFaces(dx, dy, dz); } else if (this.clock - c.zt > BAKE_HOLD_S) c.zone = ZONE_NEAR; break;
    }
  }

  /** The faces whose view a direction falls in (within 60 degrees of the face's axis) must be drawn again. */
  private markFaces(dx: number, dy: number, dz: number) {
    const L = Math.hypot(dx, dy, dz) || 1;
    for (let f = 0; f < 6; f++) { const a = FACE_A[f]; if ((dx * a[0] + dy * a[1] + dz * a[2]) / L > 0.5) this.faceForce[f] = 1; }
  }

  /** What the faces need to know of a baked cell: the nearest of them, the fastest moving, whether any is still growing in or fading out. */
  private noteFar(c: Cell, dx: number, dy: number, dz: number, ground: number) {
    const L = Math.hypot(dx, dy, dz) || 1, sp = Math.hypot(c.vx, c.vz), fad = c.pres < 0.999 || !c.alive;
    for (let f = 0; f < 6; f++) {
      const a = FACE_A[f];
      if ((dx * a[0] + dy * a[1] + dz * a[2]) / L > 0.5) {
        this.faceCells[f]++;
        if (ground < this.faceMinG[f]) this.faceMinG[f] = ground;
        if (sp > this.faceVmax[f]) this.faceVmax[f] = sp;
        if (fad) this.faceFading[f] = 1;
      }
    }
  }

  private faceVisible(f: number): boolean {
    const cp = this.cullCam.position, v = this.tmpV;
    for (const s of this.faceSamples[f]) { v.copy(s).multiplyScalar(1000).add(cp); if (this.frustum.containsPoint(v)) return true; }
    // the view may sit inside the face between the samples: its centre direction
    return this.faceOf(this.cullCam.getWorldDirection(this.tmpB)) === f;
  }

  /** The face a direction falls in (the largest axis; the same rule as the lookup's). */
  private faceOf(d: THREE.Vector3): number {
    const ax = Math.abs(d.x), ay = Math.abs(d.y), az = Math.abs(d.z);
    return ax >= ay && ax >= az ? (d.x > 0 ? 0 : 1) : ay >= az ? (d.y > 0 ? 2 : 3) : (d.z > 0 ? 4 : 5);
  }

  private lightMoved(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) if (Math.abs(a[i] - b[i]) > BAKE_LIGHT_TOL * Math.max(Math.abs(a[i]), 1e-3)) return true;
    return false;
  }

  /** Decides which faces to draw again this frame, and sets the weights with which the sky pass blends the two atlases. */
  private planBake(camera: THREE.PerspectiveCamera, dt: number) {
    this.todo.length = 0;
    const bake = this.bakeOn;
    if (!bake) { this.showMesh.visible = false; return; }
    const sig = this.lightNow; sig.length = 0;
    for (const k of LIGHT_KEYS) uniformNums(this.shared[k]?.value, sig);
    const cand: { f: number; e: number }[] = [];
    let empties = 0;
    for (let f = 0; f < 6; f++) {
      const F = this.faces[f];
      if (!F.has) empties++;
      if (!this.faceVisible(f)) continue;
      if (F.has && F.fade < 1) continue;                          // its newest picture is still fading in
      const n = this.faceCells[f];
      let e = 0;
      if (!F.has) e = n > 0 ? 1e9 : 0;
      else if (n === 0) e = F.empty ? 0 : 1e8;
      else {
        const age = this.clock - F.t, shift = camera.position.distanceTo(F.cam);
        e = this.pxPerRad * (this.faceVmax[f] * age + shift) / Math.max(300, this.faceMinG[f]);
        if (this.faceFading[f] && age > BAKE_FADING_AGE_S) e = Math.max(e, 1e6);
        if (age > BAKE_MAX_AGE_S) e = Math.max(e, 1e6);
        if (this.faceForce[f]) e = Math.max(e, 1e7);
        if (this.lightMoved(F.sig, sig)) e = Math.max(e, 5e6);
        if (e <= BAKE_ERR_PX) e = 0;
      }
      if (e > 0) cand.push({ f, e });
    }
    cand.sort((a, b) => b.e - a.e);
    const maxF = empties > 0 ? 2 : BAKE_MAX_FACES;
    for (let k = 0; k < Math.min(maxF, cand.length); k++) this.todo.push(cand[k].f);

    let any = false;
    for (let f = 0; f < 6; f++) {
      const F = this.faces[f];
      if (F.has && F.fade < 1) F.fade = Math.min(1, F.fade + dt / BAKE_XFADE_S);
      if (F.fade >= 1) F.prevHas = false;
      const w = F.fade * F.fade * (3 - 2 * F.fade), wo = F.prevHas ? 1 - w : 0;
      this.wa[f] = !F.has ? 0 : F.cur === 0 ? w : wo;
      this.wb[f] = !F.has ? 0 : F.cur === 1 ? w : wo;
      if (F.has && !F.empty) any = true;
    }
    this.showMesh.visible = any;
    this.bakeStats.faces = this.faces.filter(F => F.has).length;
  }

  /** Redraws the far puffs into the faces chosen by planBake. Runs inside the frame's render (the hook mesh draws first), or the host may call it before its render. */
  prepare(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera) {
    const sz = renderer.getDrawingBufferSize(this.tmpS);
    this.pxPerRad = (sz.y || 800) / 2 / Math.tan(camera.fov * Math.PI / 360);
    if (!this.bakeOn || this.todo.length === 0 || this.bakeFrame === this.frameNo) { this.bakeStats.lastMs = 0; return; }
    this.bakeFrame = this.frameNo;
    const t0 = performance.now();
    this.buildFar(camera);
    const prevRT = renderer.getRenderTarget(), prevAuto = renderer.autoClear, prevReset = renderer.info.autoReset, prevXr = renderer.xr.enabled, prevShadow = renderer.shadowMap.autoUpdate;
    const prevCol = renderer.getClearColor(new THREE.Color()), prevA = renderer.getClearAlpha();
    renderer.autoClear = false; renderer.info.autoReset = false; renderer.xr.enabled = false; renderer.shadowMap.autoUpdate = false; renderer.setClearColor(0x000000, 0);
    const cam = this.bakeCam, S = BAKE_FACE;
    cam.fov = BAKE_FOV; cam.aspect = 1; cam.near = 1; cam.far = 1e6; cam.updateProjectionMatrix();
    for (const f of this.todo) {
      const F = this.faces[f];
      const target = F.has ? 1 - F.cur : 0;
      const a = FACE_A[f], r = FACE_R[f], u = FACE_U[f];
      this.tmpM.makeBasis(this.tmpR.set(r[0], r[1], r[2]), this.tmpU.set(u[0], u[1], u[2]), this.tmpB.set(-a[0], -a[1], -a[2]));
      cam.quaternion.setFromRotationMatrix(this.tmpM); cam.position.copy(camera.position); cam.updateMatrixWorld(true);
      const rt = this.atlas[target];
      rt.viewport.set((f % 3) * S, Math.floor(f / 3) * S, S, S); rt.scissor.copy(rt.viewport); rt.scissorTest = true;
      renderer.setRenderTarget(rt);
      renderer.clear(true, false, false);
      renderer.render(this.bakeScene, cam);
      F.prevHas = F.has; F.cur = target; F.has = true; F.fade = 0; F.t = this.clock; F.cam.copy(camera.position); F.sig = this.lightNow.slice();
      F.empty = this.faceCells[f] === 0; this.faceForce[f] = 0;
      this.bakeStats.redraws++;
    }
    for (const rt of this.atlas) rt.scissorTest = false;
    renderer.setRenderTarget(prevRT); renderer.autoClear = prevAuto; renderer.info.autoReset = prevReset; renderer.xr.enabled = prevXr; renderer.shadowMap.autoUpdate = prevShadow;
    renderer.setClearColor(prevCol, prevA);
    this.bakeStats.lastMs = performance.now() - t0;
  }

  /** Collects every baked cell (every tile image in range, not only those in view), lights them against all cells, and writes their puffs, far to near. */
  private buildFar(camera: THREE.Camera) {
    if (this.clock - this.farBuiltAt < 0.2 && this.bakeGeo.instanceCount > 0) return;
    this.farBuiltAt = this.clock;
    const T = this.store.tile, cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
    const far = this.farList; far.length = 0;
    for (const c of this.store.cells.values()) {
      if (c.pres <= 0.01 || (c.zone !== ZONE_FAR && c.zone !== ZONE_ENTERING)) continue;
      const [x, z] = this.store.position(c);
      const grow = Math.pow(c.pres, 0.7);
      const r = Math.max(60, c.r * grow), thick = Math.max(80, (c.top - c.base) * grow);
      const bx = x - Math.round((x - cx) / T) * T, bz = z - Math.round((z - cz) / T) * T;
      for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
        const px = bx + i * T, pz = bz + j * T;
        const ground = Math.hypot(px - cx, pz - cz);
        if (ground - 1.4 * r > CELL_RANGE_FAR) continue;
        far.push({ c, px, pz, dist: Math.hypot(px - cx, c.base + thick * 0.5 - cy, pz - cz), ground, thick, r, base: c.base, vol: false, k: grow, shade: 1, baked: true });
      }
    }
    const occ = far.concat(this.vis.filter(v => !v.baked && v.c.zone !== ZONE_ENTERING));
    this.shadeCells(far, occ);
    const P = this.bP.array as Float32Array, Lc = this.bL.array as Float32Array, M = this.bM.array as Float32Array, SH = this.bS.array as Float32Array, BS = this.bB.array as Float32Array;
    let n = 0;
    for (const v of far) n = this.emitPuffs(v, n, BAKE_PUFF_CAP, P, Lc, M, SH, BS, this.bKeys, cx, cy, cz);
    farToNear(this.bKeys, n, this.bOrd);
    const tmp = this.bTmp;
    permute(P, this.bOrd, n, 4, tmp); permute(Lc, this.bOrd, n, 4, tmp); permute(M, this.bOrd, n, 4, tmp); permute(SH, this.bOrd, n, 1, tmp); permute(BS, this.bOrd, n, 2, tmp);
    this.bakeGeo.instanceCount = n;
    this.bP.needsUpdate = this.bL.needsUpdate = this.bM.needsUpdate = this.bS.needsUpdate = this.bB.needsUpdate = true;
    this.bakeStats.puffs = n;
  }

  /** The share of the beam each target cell keeps past the occluding cells: a ray from the cell toward the light crosses the cells that lie that way, each
   *  costing its optical depth times the share of its width the ray cuts, in the units of the puff shader's own self-shadowing (SUN_ABSORB per unit of tau).
   *  Cells in the middle of a bank, or under a taller one, are darker than the cells at its sunward edge. Skipped with the light near or below the horizon. */
  private shadeCells(targets: Vis[], occ: Vis[]) {
    const L = this.lightDir, hl = Math.hypot(L.x, L.z);
    if (L.y < 0.1 || hl < 1e-4) return;
    const lx = L.x / hl, lz = L.z / hl, rise = L.y / hl;
    const tauOf = (c: Cell) => Math.max(2, c.lwp * cloudOptics.tauPerGram());
    for (const a of targets) {
      const oy = a.base + 0.4 * a.thick;
      let k = 0;
      for (const b of occ) {
        if (b === a) continue;
        const dx = b.px - a.px, dz = b.pz - a.pz;
        const along = dx * lx + dz * lz;
        if (along <= 0 || along > 9000) continue;
        const perp = Math.abs(dx * lz - dz * lx);
        if (perp >= b.r) continue;
        const rp = perp / b.r, ext = b.base + b.thick * Math.sqrt(1 - rp * rp), h = oy + along * rise;
        if (h < b.base - 0.2 * b.thick) continue;
        const s = Math.max(0, Math.min(1, (ext - h) / (0.3 * b.thick)));
        if (s <= 0) continue;
        k += tauOf(b.c) * 2 * Math.sqrt(1 - rp * rp) * s * b.c.pres;
      }
      a.shade = Math.exp(-SUN_ABSORB * k);
    }
  }

  private tmp = new Float32Array(PUFF_CAP * 4);

  dispose() {
    this.puffGeo.dispose(); this.volGeo.dispose(); this.rainGeo.dispose(); this.bakeGeo.dispose(); this.noise.dispose();
    for (const m of [this.puffMesh, this.volMesh, this.rainMesh, this.bakeMesh, this.showMesh, this.hookMesh]) { (m.material as THREE.Material).dispose(); }
    this.showMesh.geometry.dispose(); this.hookMesh.geometry.dispose();
    for (const rt of this.atlas) rt.dispose();
  }
}
